/**
 * ApiDocsPage — API 文档/调试器（P5）
 * 结构化展示对外网关 API 契约（POST /api/v1/gateway/send），并内置试发面板：
 * 用接入客户端的 AppKey + HMAC-SHA256 签名调一次真实发信，验证契约。
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Field,
  InfoLabel,
  Input,
  Combobox,
  Option,
  Button,
  Spinner,
  Badge,
  Divider,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@fluentui/react-components'
import {
  SendRegular,
  Code20Regular,
  KeyRegular,
  DocumentTextRegular,
  DataUsage20Regular,
  CopyRegular,
  RocketRegular,
} from '@fluentui/react-icons'
import { clientsApi } from '@/api/clients'
import { templatesApi } from '@/api/templates'
import { domainsApi } from '@/api/domains'
import { toast } from '@/lib/toast'
import KeyValueEditor from '@/components/KeyValueEditor'
import axios from 'axios'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  docCard: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  docCardHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  docCardTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  endpointBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '12px 14px',
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  endpointPath: {
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: tokens.fontSizeBase300,
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: tokens.fontWeightSemibold },
  sectionHeaderIcon: { color: tokens.colorBrandForeground1 },
  codeBlock: {
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: '13px',
    lineHeight: '1.55',
    whiteSpace: 'pre',
    wordBreak: 'normal',
    overflowX: 'auto',
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 14px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  paramTable: { marginTop: '4px' },
  paramName: {
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  form: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  item: { minWidth: 0 },
  colSpan2: { gridColumn: '1 / -1' },
  result: {
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px',
    maxHeight: '280px',
    overflow: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  resultHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  copyBtn: { minWidth: '0', padding: '0 6px' },
  // ── 接入向导 ──
  wizardRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  wizardIndex: {
    width: '26px',
    height: '26px',
    flexShrink: 0,
    borderRadius: '50%',
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: tokens.fontSizeBase300,
  },
  wizardBody: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 },
  wizardTitle: { fontWeight: tokens.fontWeightSemibold, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  wizardDesc: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, lineHeight: 1.5 },
  wizardColumn: { display: 'flex', flexDirection: 'column', gap: '16px' },
  lifecycleBar: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: tokens.fontSizeBase200 },
  lifecycleChip: {
    padding: '2px 8px',
    borderRadius: tokens.borderRadiusSmall,
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
  },
  lifecycleChipActive: {
    background: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  overviewTable: { marginTop: '4px' },
})

/** HMAC-SHA256（十六进制），用 Web Crypto 计算 */
async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const enc = new TextEncoder()
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message))
  return Array.from(new Uint8Array(sig)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

const ENDPOINT_PATH = '/api/v1/gateway/send'

/** 第三方邮件接入（注册类）接口契约 */
interface RegistryEndpoint {
  method: string
  path: string
  desc: string
  body: string
  note: string
  success: string
}

const REGISTRY_ENDPOINTS: RegistryEndpoint[] = [
  {
    method: 'POST',
    path: '/api/v1/gateway/users',
    desc: '注册邮箱：在客户端所属收件域下创建一个邮箱账号，自动建标准邮箱文件（INBOX/Sent/...）。未指定密码时系统生成随机密码。',
    body: `{
  "username": "alice",          // 邮箱用户名（必填）
  "password": "S3cret!",        // 密码（可选，缺省自动生成）
  "display_name": "Alice",      // 显示名（可选）
  "quota_bytes": 1073741824     // 配额字节（可选）
}`,
    note: '只能创建到客户端自身绑定的收件域下，实现按域隔离。',
    success: `{
  "success": true,
  "data": { "id": 12, "email": "alice@example.com", "domainId": 3 }
}`,
  },
  {
    method: 'POST',
    path: '/api/v1/gateway/templates',
    desc: '注册模板：在客户端所属收件域下创建邮件模板。新模板为草稿（draft），需管理端审核通过且启用后才能发信。',
    body: `{
  "name": "验证码邮件",          // 模板名称（必填）
  "subject": "您的验证码是 {{code}}", // 主题（必填）
  "body_html": "<p>您好 {{name}}，验证码 {{code}}</p>", // HTML 正文（可选）
  "body_text": "您好 {{name}}，验证码 {{code}}",        // 纯文本（可选）
  "variables": [{ "name": "code", "desc": "验证码" }]  // 变量定义（可选）
}`,
    note: '模板归属客户端绑定域；审核通过（approved）且启用（active=1）后才能被发信接口调用。',
    success: `{
  "success": true,
  "data": { "id": 21, "domainId": 3, "status": "draft" }
}`,
  },
  {
    method: 'PUT',
    path: '/api/v1/gateway/templates/:id',
    desc: '修改模板：仅可修改客户端自己域内的模板；成功修改后模板回到草稿（draft），需重新审核。',
    body: `{
  "name": "验证码邮件（新）",   // 全字段可选，仅提交要修改的字段
  "subject": "您的验证码是 {{code}}",
  "body_html": "<p>...</p>",
  "body_text": "...",
  "variables": [{ "name": "code", "desc": "验证码" }]
}`,
    note: 'path 参数 :id 为模板 ID；若模板不属于该客户端域则返回 404。',
    success: `{
  "success": true,
  "data": { "id": 21, "status": "draft", "updated": true }
}`,
  },
  {
    method: 'PUT',
    path: '/api/v1/gateway/templates/:id/active',
    desc: '停用/启用模板：切换 active 开关（0=停用，1=启用）。停用后即使审核通过也不能发信。',
    body: `{
  "active": false   // false=停用，true=启用
}`,
    note: '与审核状态（draft/pending/approved/rejected）正交；仅能操作客户端自己域内的模板。',
    success: `{
  "success": true,
  "data": { "id": 21, "active": false }
}`,
  },
  {
    method: 'POST',
    path: '/api/v1/gateway/webhooks',
    desc: '注册收件回调：在客户端所属收件域配置 webhook，收到该域邮件时异步 POST 到 targetUrl。',
    body: `{
  "target_url": "https://your.site/hook/inbound", // 回调地址（必填）
  "format": "json"                                // json | multipart（可选，默认 json）
}`,
    note: '回调域强制取客户端所属收件域，不可跨域配置。收到邮件后系统会异步推送事件到该地址。',
    success: `{
  "success": true,
  "data": { "id": 7, "domain": "example.com", "format": "json" }
}`,
  },
]

/** 对外网关全量接口一览（含发信） */
interface EndpointOverview {
  method: string
  path: string
  desc: string
  category: '发信' | '邮箱' | '模板' | '回调'
}

const ENDPOINT_OVERVIEW: EndpointOverview[] = [
  { method: 'POST', path: '/api/v1/gateway/send', desc: '基于模板发送邮件', category: '发信' },
  { method: 'POST', path: '/api/v1/gateway/users', desc: '注册邮箱账号', category: '邮箱' },
  { method: 'POST', path: '/api/v1/gateway/templates', desc: '注册邮件模板', category: '模板' },
  { method: 'PUT', path: '/api/v1/gateway/templates/:id', desc: '修改邮件模板', category: '模板' },
  { method: 'PUT', path: '/api/v1/gateway/templates/:id/active', desc: '停用/启用模板', category: '模板' },
  { method: 'POST', path: '/api/v1/gateway/webhooks', desc: '注册收件回调', category: '回调' },
]

/** 接入向导：面向第三方的一句话逐步文案 */
interface WizardStep {
  title: string
  desc: string
  endpoint?: string
  optional?: boolean
}

const ONBOARDING_STEPS: WizardStep[] = [
  {
    title: '创建接入客户端，获取凭据',
    desc: '在管理端「接入客户端」中新建并审核通过一个客户端，记下系统生成的 AppKey 与 AppSecret。AppSecret 用于签名鉴权、不应外传；确认客户端已绑定一个收件域，你后续注册的所有资源都将归属该域。',
  },
  {
    title: '注册邮箱账号',
    endpoint: 'POST /api/v1/gateway/users',
    desc: '在绑定的收件域下创建邮箱账号，作为发件人身份与收件身份。未指定密码时系统自动生成随机密码；每个账号会自动建好 INBOX/Sent 等标准邮箱文件。',
  },
  {
    title: '注册邮件模板',
    endpoint: 'POST /api/v1/gateway/templates',
    desc: '上传你的邮件模板（名称、主题、HTML/纯文本正文、可变参数）。新模板为草稿态（draft），需要通过审核并启用后才能被发信接口调用。',
  },
  {
    title: '管理端审核并启用模板',
    desc: '在管理端「邮件模板」中对草稿模板进行「提交待审」→「审核通过」（approved）；再将其「启用」（active=1）。只有同时满足 status=approved 且 active=1 的模板才能用于发信。',
  },
  {
    title: '注册收件回调（可选）',
    endpoint: 'POST /api/v1/gateway/webhooks',
    desc: '如需把收到的邮件异步推送到你的服务，注册一个收件回调地址。系统收到绑定域的新邮件后会自动 POST 推送，格式可选 json 或 multipart。',
    optional: true,
  },
  {
    title: '开始发信并接收回调',
    endpoint: 'POST /api/v1/gateway/send',
    desc: '调用发信接口，用注册时的模板 + 邮箱身份发送邮件，请求体里的变量会按模板占位符渲染；同时，凡是发往该收件域的邮件都会按你注册的回调推送。',
  },
]

const NODE_EXAMPLE = `const crypto = require('crypto');

const body = JSON.stringify({
  template_id: 1,
  from: 'sender@example.com',
  recipients: ['a@example.com'],
  variables: { name: '张三' },
});

const ts = Date.now();
// 签名串 = 时间戳 + '.' + 原始请求体（必须与 body 完全一致）
const sig = crypto
  .createHmac('sha256', appSecret)
  .update(ts + '.' + body)
  .digest('hex');

const res = await fetch('https://<host>/api/v1/gateway/send', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-App-Key': appKey,
    'X-Timestamp': String(ts),
    'X-Signature': sig,
  },
  body,
});`

const REQUEST_BODY = `{
  "template_id": 1,                    // 已审核的邮件模板 ID
  "from": "sender@example.com",        // 发件人（必须为本地收件域邮箱）
  "recipients": ["a@example.com"],     // 收件人列表（至少 1 个）
  "variables": { "name": "张三" }      // 模板变量（可选）
}`

const RESPONSE_BODY = `{
  "success": true,
  "data": {
    "template": "订单通知",          // 模板名称
    "subject": "您的订单已发货",     // 渲染后的主题
    "total": 2,                      // 收件人总数
    "sent": 2,                       // 成功发送数
    "failed": 0,                     // 失败数
    "results": [                     // 逐收件人结果
      { "recipient": "a@example.com", "status": "sent" },
      { "recipient": "b@example.com", "status": "queued" }
    ]
  }
}`

export default function ApiDocsPage() {
  const styles = useStyles()

  const { data: clients } = useQuery({ queryKey: ['clients-doc'], queryFn: () => clientsApi.list() })
  const { data: templates } = useQuery({ queryKey: ['templates-doc'], queryFn: () => templatesApi.list() })
  const { data: domains } = useQuery({ queryKey: ['domains-doc'], queryFn: () => domainsApi.list() })

  const approvedTemplates = useMemo(
    () => (templates ?? []).filter((t) => t.status === 'approved' && t.active),
    [templates],
  )

  // 网关认证只认「已审核通过且启用」的客户端，仅展示这些避免试发 401
  const approvedClients = useMemo(
    () => (clients ?? []).filter((c) => c.status === 'approved' && c.active),
    [clients],
  )

  // 试发面板状态
  const [clientId, setClientId] = useState<number | null>(null)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [from, setFrom] = useState('')
  const [recipients, setRecipients] = useState('')
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const fromOptions = useMemo(
    () => (domains ?? []).map((d) => `postmaster@${d.name}`),
    [domains],
  )

  const selectedClient = clients?.find((c) => c.id === clientId) ?? null

  const handleSend = async () => {
    if (!selectedClient) {
      toast.error('请选择接入客户端')
      return
    }
    if (templateId == null) {
      toast.error('请选择已审核的模板')
      return
    }
    const rcpts = recipients.split(/[,，;；\s]+/).map((s) => s.trim()).filter(Boolean)
    if (rcpts.length === 0) {
      toast.error('请填写至少一个收件人')
      return
    }
    let vars: Record<string, string> = {}
    for (const [k, v] of Object.entries(variables)) {
      if (k.trim()) vars[k.trim()] = v
    }

    setSending(true)
    setResult(null)
    try {
      // 用 AppKey + HMAC 签名调用（与文档契约一致）。用独立 axios 而非 apiClient：
      // 试发是「接入客户端」身份，若网关校验失败返回 401，不应牵连管理员登录态
      // （走 apiClient 会清 token 跳 /login，误触发 OAuth 重登）。
      const body = JSON.stringify({
        template_id: templateId,
        from,
        recipients: rcpts,
        variables: vars,
      })
      const ts = Date.now()
      const signature = await hmacSha256Hex(selectedClient.app_secret ?? '', `${ts}.${body}`)
      const resp = await axios.post('/api/v1/gateway/send', body, {
        headers: {
          'Content-Type': 'application/json',
          'X-App-Key': selectedClient.app_key ?? '',
          'X-Timestamp': String(ts),
          'X-Signature': signature,
        },
      })
      const resBody = resp.data as { success?: boolean; error?: string }
      if (resBody && resBody.success === false) {
        throw new Error(resBody.error || '发送失败')
      }
      setResult(JSON.stringify(resp.data, null, 2))
      toast.success('发送成功', `共 ${rcpts.length} 个收件人`)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string }
      const msg = axiosErr?.response?.data?.error || axiosErr?.message || '发送失败'
      toast.error('发送失败', msg)
      setResult(
        axiosErr?.response?.data
          ? JSON.stringify(axiosErr.response.data, null, 2)
          : String(err),
      )
    } finally {
      setSending(false)
    }
  }

  const copyText = (text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success('已复制'),
      () => toast.error('复制失败'),
    )
  }

  return (
    <div className={styles.root}>
      <Text style={{ fontSize: '20px', fontWeight: 700, color: tokens.colorNeutralForeground1 }}>
        API 文档/调试器
      </Text>

      {/* ① 接入向导 */}
      <Card className={styles.docCard}>
        <div className={styles.docCardHeader}>
          <RocketRegular style={{ color: tokens.colorBrandForeground1 }} />
          <Text className={styles.docCardTitle}>接入向导</Text>
          <Badge appearance="tint" color="success">快速上手</Badge>
        </div>

        <MessageBar intent="info">
          <MessageBarBody>
            只需在管理端「接入客户端」登记一次，即可用同一套 <b>AppKey/AppSecret</b> 凭据完成<b>发信</b>与<b>收件</b>的完整接入。所有资源都绑定到客户端所属的收件域，杜绝跨域混用。按下面 6 步即可上线。
          </MessageBarBody>
        </MessageBar>

        <div className={styles.wizardColumn}>
          {ONBOARDING_STEPS.map((step, i) => (
            <div key={step.title} className={styles.wizardRow}>
              <div className={styles.wizardIndex}>{i + 1}</div>
              <div className={styles.wizardBody}>
                <div className={styles.wizardTitle}>
                  {step.title}
                  {step.optional && <Badge appearance="outline" color="informative">可选</Badge>}
                </div>
                {step.endpoint && (
                  <div className={styles.endpointBlock} style={{ padding: '6px 10px' }}>
                    <Badge appearance="filled" color={step.endpoint.startsWith('POST') ? 'brand' : 'warning'}>
                      {step.endpoint.split(' ')[0]}
                    </Badge>
                    <span className={styles.endpointPath} style={{ fontSize: tokens.fontSizeBase200 }}>{step.endpoint.split(' ')[1]}</span>
                  </div>
                )}
                <div className={styles.wizardDesc}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ② API 契约文档 */}
      <Card className={styles.docCard}>
        <div className={styles.docCardHeader}>
          <Code20Regular style={{ color: tokens.colorBrandForeground1 }} />
          <Text className={styles.docCardTitle}>对外发信 API 契约</Text>
          <Badge appearance="tint" color="brand">网关</Badge>
        </div>

        {/* 端点 */}
        <div className={styles.endpointBlock}>
          <Badge appearance="filled" color="brand">POST</Badge>
          <span className={styles.endpointPath}>{ENDPOINT_PATH}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>发送一封基于模板的邮件</Text>
        </div>

        {/*
          ── 鉴权 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <KeyRegular className={styles.sectionHeaderIcon} />
            <Text>鉴权方式（推荐：AppKey + HMAC-SHA256 签名）</Text>
          </div>
          <MessageBar intent="info">
            <MessageBarBody>
              接入客户端的 <b>AppKey</b> 与 <b>AppSecret</b> 在「接入客户端」创建时由系统生成，仅创建成功弹窗完整展示一次；之后可在客户端详情中再次查看（明文存储）。请妥善保管，AppSecret 用于签名、不应外传。
            </MessageBarBody>
          </MessageBar>
          <Table className={styles.paramTable} size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>请求头</TableHeaderCell>
                <TableHeaderCell>说明</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell><span className={styles.paramName}>X-App-Key</span></TableCell>
                <TableCell>接入客户端的 AppKey</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><span className={styles.paramName}>X-Timestamp</span></TableCell>
                <TableCell>当前毫秒时间戳，用于防止重放</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><span className={styles.paramName}>X-Signature</span></TableCell>
                <TableCell><code>hex( HMAC-SHA256(appSecret, "&lt;X-Timestamp&gt;.&lt;原始请求体&gt;") )</code></TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Text className={styles.hint}>
            兼容：旧式直连仍可用单头 <code>X-API-Key: &lt;API Key&gt;</code> 鉴权（向后兼容）。
          </Text>
        </div>

        <Divider />

        {/*
          ── 请求体 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <DocumentTextRegular className={styles.sectionHeaderIcon} />
            <Text>请求体（JSON）</Text>
          </div>
          <pre className={styles.codeBlock}>{REQUEST_BODY}</pre>
        </div>

        <Divider />

        {/*
          ── 响应 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <DataUsage20Regular className={styles.sectionHeaderIcon} />
            <Text>响应（成功 <code>200</code>）</Text>
          </div>
          <pre className={styles.codeBlock}>{RESPONSE_BODY}</pre>
          <Text className={styles.hint}>
            常见错误：<code>400</code> 参数非法 / 非本地发件域；<code>401</code> 鉴权失败；<code>403</code> 模板未审核或已停用；<code>404</code> 模板不存在。
          </Text>
        </div>

        <Divider />

        {/*
          ── 调用示例 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Code20Regular className={styles.sectionHeaderIcon} />
            <Text>调用示例（Node.js）</Text>
          </div>
          <pre className={styles.codeBlock}>{NODE_EXAMPLE}</pre>
        </div>
      </Card>

      {/* ② 第三方邮件接入：注册类接口 */}
      <Card className={styles.docCard}>
        <div className={styles.docCardHeader}>
          <KeyRegular style={{ color: tokens.colorBrandForeground1 }} />
          <Text className={styles.docCardTitle}>第三方邮件接入（注册资源）</Text>
          <Badge appearance="tint" color="brand">网关</Badge>
        </div>

        <MessageBar intent="info">
          <MessageBarBody>
            接入客户端的 <b>AppKey/AppSecret</b> 在「接入客户端」创建时生成。这些注册类接口与发信接口共用同一套鉴权，且<b>所有资源都绑定到客户端所属的收件域</b>——第三方只需在接入客户端登记后，即可用同一套凭据注册邮箱、模板与收件回调，完整接入邮件能力。
          </MessageBarBody>
        </MessageBar>

        {/*
          ── 接口一览 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <DataUsage20Regular className={styles.sectionHeaderIcon} />
            <Text>对外网关接口一览</Text>
          </div>
          <Table className={styles.overviewTable} size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>方法</TableHeaderCell>
                <TableHeaderCell>路径</TableHeaderCell>
                <TableHeaderCell>说明</TableHeaderCell>
                <TableHeaderCell>分类</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ENDPOINT_OVERVIEW.map((e) => (
                <TableRow key={e.method + e.path}>
                  <TableCell><Badge appearance="filled" color={e.method === 'POST' ? 'brand' : 'warning'}>{e.method}</Badge></TableCell>
                  <TableCell><span className={styles.paramName}>{e.path}</span></TableCell>
                  <TableCell>{e.desc}</TableCell>
                  <TableCell><Badge appearance="tint" color="informative">{e.category}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Divider />

        {/*
          ── 模板状态生命周期 ──
        */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <DocumentTextRegular className={styles.sectionHeaderIcon} />
            <Text>模板状态生命周期（审核与启停正交）</Text>
          </div>
          <div className={styles.lifecycleBar}>
            审核状态：<span className={styles.lifecycleChip}>draft 草稿</span>
            <span aria-hidden>→</span>
            <span className={styles.lifecycleChip}>pending 待审核</span>
            <span aria-hidden>→</span>
            <span className={styles.lifecycleChip}>approved 已通过</span>
            <span aria-hidden>｜</span>
            <span className={styles.lifecycleChip}>rejected 已驳回</span>
          </div>
          <div className={styles.lifecycleBar}>
            启用开关：<span className={styles.lifecycleChip}>active=0 停用</span>
            <span aria-hidden>⇄</span>
            <span className={styles.lifecycleChipActive}>active=1 启用</span>
          </div>
          <Text className={styles.hint}>
            一张模板同时具备「审核状态」与「启用开关」两个维度，二者相互独立。<b>能被发信接口调用的前提：status=approved（审核通过）且 active=1（启用）</b>。修改模板内容会回到 draft，需重新走审核流程；停用/启用模板则随时可切换，不影响审核状态。
          </Text>
        </div>

        <Divider />

        {REGISTRY_ENDPOINTS.map((ep) => (
          <div key={ep.method + ep.path} className={styles.section}>
            <div className={styles.endpointBlock}>
              <Badge appearance="filled" color={ep.method === 'POST' ? 'brand' : 'warning'}>{ep.method}</Badge>
              <span className={styles.endpointPath} style={{ fontWeight: 600 }}>{ep.path}</span>
            </div>
            <Text>{ep.desc}</Text>
            <Text className={styles.hint}>同前述鉴权：<code>X-App-Key</code> + <code>X-Timestamp</code> + <code>X-Signature</code>（或兼容的单头 <code>X-API-Key</code>）。</Text>
            <Text weight="semibold" size={200}>请求体（JSON）</Text>
            <pre className={styles.codeBlock}>{ep.body}</pre>
            <Text weight="semibold" size={200}>成功响应</Text>
            <pre className={styles.codeBlock}>{ep.success}</pre>
            <Text className={styles.hint}>{ep.note}</Text>
          </div>
        ))}

        <Divider />

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Code20Regular className={styles.sectionHeaderIcon} />
            <Text>注册流程示意</Text>
          </div>
          <Text className={styles.hint}>
            ① 管理端「接入客户端」创建客户端并审核通过 → ② 第三方调用注册邮箱/注册模板/注册收件回调 → ③ 管理端在「邮件模板」中审核并启用模板 → ④ 第三方调用 <code>POST /api/v1/gateway/send</code> 发信，同时收件回调自动推送。
          </Text>
        </div>
      </Card>

      {/* ③ 页内试发面板 */}
      <Card className={styles.docCard}>
        <div className={styles.docCardHeader}>
          <SendRegular style={{ color: tokens.colorBrandForeground1 }} />
          <Text className={styles.docCardTitle}>页内试发</Text>
        </div>

        <div className={styles.form}>
          <Field
            className={styles.item}
            label={<InfoLabel info="作为发信身份使用的接入客户端，其 AppKey/AppSecret 将用于本次签名。仅已审核通过且已启用的客户端可被调用。">接入客户端</InfoLabel>}
            required
          >
            <Combobox
              value={selectedClient?.name ?? ''}
              onOptionSelect={(_, d) => setClientId(Number(d.optionValue))}
              placeholder="选择已启用客户端"
            >
              {approvedClients.map((c) => (
                <Option key={c.id} value={String(c.id)}>{c.name}</Option>
              ))}
            </Combobox>
          </Field>

          <Field
            className={styles.item}
            label={<InfoLabel info="仅已审核通过且已启用的模板可被调用。">邮件模板</InfoLabel>}
            required
          >
            <Combobox
              value={approvedTemplates.find((t) => t.id === templateId)?.name ?? ''}
              onOptionSelect={(_, d) => setTemplateId(Number(d.optionValue))}
              placeholder="选择已审核模板"
            >
              {approvedTemplates.map((t) => (
                <Option key={t.id} value={String(t.id)}>{t.name}</Option>
              ))}
            </Combobox>
          </Field>

          <Field
            className={styles.item}
            label={<InfoLabel info="发件人，必须是本地收件域邮箱。">发件人</InfoLabel>}
            required
          >
            <Combobox
              value={from}
              freeform
              onOptionSelect={(_, d) => setFrom(d.optionValue ?? '')}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="sender@example.com"
            >
              {fromOptions.map((em) => <Option key={em} value={em}>{em}</Option>)}
            </Combobox>
          </Field>

          <Field
            className={styles.item}
            label={<InfoLabel info="多个用逗号/分号/空格分隔。">收件人</InfoLabel>}
            required
          >
            <Input
              value={recipients}
              onChange={(_, d) => setRecipients(d.value)}
              placeholder="a@example.com, b@example.com"
            />
          </Field>

          <Field
            className={styles.colSpan2}
            label={<InfoLabel info="模板变量，按「变量名=值」逐行填写，无需手写 JSON。可选。">变量</InfoLabel>}
          >
            <KeyValueEditor value={variables} onChange={setVariables} />
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button
            appearance="primary"
            icon={<SendRegular />}
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? <Spinner size="tiny" /> : '发送'}
          </Button>
        </div>

        {result != null && (
          <div>
            <div className={styles.resultHeader}>
              <Text weight="semibold">返回结果</Text>
              <Button
                appearance="subtle"
                size="small"
                icon={<CopyRegular />}
                className={styles.copyBtn}
                onClick={() => copyText(result)}
              >
                复制
              </Button>
            </div>
            <pre className={styles.result}>{result}</pre>
          </div>
        )}
      </Card>
    </div>
  )
}